import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function Privacy() {
  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc]" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm border border-slate-200"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 font-['Oswald']">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm mb-8 pb-6 border-b border-slate-100">
            Effective Date: 21 July 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
            <p>
              Welcome to VHFMS (Venkateswara Hospitality & Facilities Management Services).
            </p>
            <p>
              VHFMS respects your privacy and is committed to protecting the personal information you provide when using our mobile application, website, and related services.
            </p>
            <p>
              This Privacy Policy explains what information we collect, how we use it, how we protect it, when it may be shared, how long we retain it, and how you can request deletion of your account and associated personal information.
            </p>
            <p>
              By accessing or using VHFMS services, you acknowledge that you have read and understood this Privacy Policy.
            </p>

            {/* 1. Information We Collect */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3 font-['Oswald']">1. Information We Collect</h2>
              <p className="mb-4">Depending on the features you use, VHFMS may collect the following information.</p>

              <div className="space-y-4 pl-2">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">1.1 Account Information</h3>
                  <p className="mb-2 text-sm text-slate-600">When you create a VHFMS customer account, we may collect:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>Full Name</li>
                    <li>Mobile Number</li>
                    <li>Password information</li>
                    <li>Account creation date</li>
                    <li>Account status</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500 italic">
                    Your password is stored in protected/hashed form and is not intended to be stored as readable plain text.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">1.2 Profile Information</h3>
                  <p className="mb-2 text-sm text-slate-600">If you choose to complete your customer profile, we may collect:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>Email Address</li>
                    <li>Gender</li>
                    <li>Address</li>
                    <li>Profile Picture</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500 italic">
                    Providing optional profile information is only necessary when required for the relevant feature or service.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">1.3 Service and Booking Information</h3>
                  <p className="mb-2 text-sm text-slate-600">
                    When you use VHFMS services, we may collect information related to your requests and bookings, including:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>Booking details</li>
                    <li>Selected services</li>
                    <li>Preferred date and time</li>
                    <li>Service address</li>
                    <li>Booking status</li>
                    <li>Assigned service professional information</li>
                    <li>Booking history</li>
                    <li>Customer reviews or feedback related to services</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">1.4 Location and Address Information</h3>
                  <p className="mb-2 text-sm text-slate-600">Depending on the services you use, you may provide:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>City or service location</li>
                    <li>Service address</li>
                    <li>Other location information necessary to provide or manage a requested service</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500 italic">
                    VHFMS uses location or address information only for purposes related to providing the requested services and managing service availability.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">1.5 Information You Provide to Customer Support</h3>
                  <p className="mb-2 text-sm text-slate-600">
                    If you contact us for support, we may collect information you provide in connection with your request, such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>Your name</li>
                    <li>Mobile number</li>
                    <li>Booking information</li>
                    <li>Issue or support request</li>
                    <li>Other information voluntarily provided by you</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 2. How We Use Your Information */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">2. How We Use Your Information</h2>
              <p className="mb-2">We may use your information to:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Create and manage your VHFMS account.</li>
                <li>Authenticate your account and allow you to securely sign in.</li>
                <li>Process and manage service bookings and requests.</li>
                <li>Provide requested services.</li>
                <li>Assign service professionals to bookings.</li>
                <li>Communicate booking confirmations, updates, and service-related notifications.</li>
                <li>Respond to customer support requests.</li>
                <li>Maintain booking and service history.</li>
                <li>Process customer reviews, feedback, and ratings.</li>
                <li>Improve our services and application.</li>
                <li>Detect, prevent, and investigate fraud, abuse, or unauthorized activity.</li>
                <li>Maintain the security and functionality of our services.</li>
                <li>Comply with applicable legal obligations.</li>
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                We do not use your personal information for purposes unrelated to the services described in this Privacy Policy without appropriate notice or consent where required.
              </p>
            </div>

            {/* 3. How We Share Your Information */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">3. How We Share Your Information</h2>
              <p className="mb-3">We do not sell or rent your personal information.</p>
              <p className="mb-3">We may share information when necessary to provide our services, including with:</p>

              <div className="space-y-3 pl-2">
                <div>
                  <h3 className="font-semibold text-slate-900">Service Professionals</h3>
                  <p className="text-sm">
                    Information necessary to complete a requested service may be shared with the service professional assigned to your booking.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Technology and Service Providers</h3>
                  <p className="text-sm mb-2">We may use trusted third-party technology providers for services such as:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                    <li>Cloud database and storage</li>
                    <li>Application infrastructure</li>
                    <li>Authentication and security-related services</li>
                    <li>Communication and notification services</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500">
                    These providers may process information on our behalf as necessary to provide and maintain the VHFMS services.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Legal Requirements</h3>
                  <p className="text-sm">
                    We may disclose information when required to do so by applicable law, legal process, governmental authority, or when reasonably necessary to protect the rights, safety, security, or property of VHFMS, our users, or others.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. Data Security */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">4. Data Security</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm">
                <li>
                  We use appropriate technical and organizational measures designed to protect your personal information against unauthorized access, alteration, disclosure, misuse, or destruction.
                </li>
                <li>Account passwords are protected using password hashing mechanisms.</li>
                <li>
                  Data transmitted between the application and our service infrastructure is protected using secure communication technologies where supported.
                </li>
                <li>However, no method of electronic storage or transmission over the internet can be guaranteed to be completely secure.</li>
              </ul>
            </div>

            {/* 5. Third-Party Services */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">5. Third-Party Services</h2>
              <p className="mb-2">
                VHFMS may use trusted third-party services and cloud infrastructure to operate and maintain the application and related services.
              </p>
              <p className="mb-2 text-sm">These services may process information necessary for:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Cloud database operations</li>
                <li>Secure data storage</li>
                <li>Application functionality</li>
                <li>Authentication and account management</li>
                <li>Communication and service delivery</li>
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                We expect third-party service providers handling user information to process such information only for legitimate service-related purposes and in accordance with applicable requirements.
              </p>
            </div>

            {/* 6. Permissions */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">6. Permissions</h2>
              <p className="mb-2">
                Depending on the features available in the application and how you use them, VHFMS may request certain device permissions.
              </p>
              <p className="mb-2 text-sm font-semibold">These may include:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li><strong>Internet access</strong> — required for communication with VHFMS services.</li>
                <li><strong>Notifications</strong> — where enabled, to provide service-related notifications.</li>
                <li><strong>Camera or photo/storage access</strong> — where required for features that allow you to upload a profile picture or other permitted content.</li>
                <li><strong>Location access</strong> — only where required by a specific feature and where permission is requested from you.</li>
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                Permissions are requested only when necessary for the applicable functionality. You can manage applicable permissions through your device settings.
              </p>
            </div>

            {/* 7. Account Deletion and Data Deletion */}
            <div className="p-6 bg-blue-50/50 rounded-xl border border-blue-100">
              <h2 className="text-xl font-bold text-slate-900 mb-3 font-['Oswald']">7. Account Deletion and Data Deletion</h2>
              <p className="mb-4 text-sm font-medium">VHFMS provides users with the ability to delete their customer account.</p>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">In-App Account Deletion</h3>
                  <p className="text-sm mb-2">Customers can access the account deletion option from within the VHFMS application.</p>
                  <p className="text-sm mb-2">
                    When an account deletion request is successfully completed, the account is permanently deactivated/deleted from active customer use and associated personal information is deleted or anonymized as applicable.
                  </p>
                  <p className="text-xs font-semibold text-slate-700 mb-1">This may include:</p>
                  <ul className="list-disc pl-5 space-y-1 text-xs text-slate-600">
                    <li>Name</li>
                    <li>Mobile number</li>
                    <li>Email address</li>
                    <li>Password information</li>
                    <li>Address</li>
                    <li>Gender</li>
                    <li>Profile picture</li>
                    <li>Customer notifications</li>
                    <li>Customer support records</li>
                    <li>Customer feedback information</li>
                    <li>Customer-related booking information that can be deleted or anonymized</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500 italic">
                    Certain information may be retained or anonymized where necessary for legitimate purposes such as legal compliance, fraud prevention, security, dispute resolution, or maintaining necessary business records.
                  </p>
                </div>

                <div className="pt-3 border-t border-blue-200/60">
                  <h3 className="font-semibold text-slate-900 mb-1">Web Account Deletion</h3>
                  <p className="text-sm mb-2">
                    Users who have uninstalled the application or cannot access the application may also initiate account deletion through our website:
                  </p>
                  <p className="font-bold text-sm my-2">
                    👉 <Link to="/delete-account" className="text-blue-700 hover:underline">https://www.vhfms.com/delete-account</Link>
                  </p>
                  <p className="text-xs text-slate-600">
                    The account deletion page provides a clear method for requesting account deletion without requiring the user to reinstall the application.
                  </p>
                  <p className="mt-2 text-xs text-slate-500 italic">
                    Google Play requires apps that support account creation to provide both an in-app deletion path and an external web resource for account deletion.
                  </p>
                </div>
              </div>
            </div>

            {/* 8. Data Retention */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">8. Data Retention</h2>
              <p className="mb-2 text-sm">
                We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Providing requested services.</li>
                <li>Maintaining necessary service and booking records.</li>
                <li>Providing customer support.</li>
                <li>Preventing fraud and misuse.</li>
                <li>Resolving disputes.</li>
                <li>Maintaining security.</li>
                <li>Complying with legal, regulatory, or other legitimate obligations.</li>
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                When information is no longer required for these purposes, we will delete, anonymize, or otherwise securely dispose of it where reasonably practicable. If certain information must be retained for legitimate legal, security, fraud-prevention, or regulatory reasons after an account deletion request, such retention will be limited to what is necessary for that purpose.
              </p>
            </div>

            {/* 9. Children's Privacy */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">9. Children's Privacy</h2>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>VHFMS services are not directed toward children under the age of 13.</li>
                <li>We do not knowingly collect personal information from children under 13.</li>
                <li>
                  If we become aware that personal information from a child under 13 has been collected without appropriate authorization, we will take reasonable steps to delete the information.
                </li>
              </ul>
            </div>

            {/* 10. Your Choices and Rights */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">10. Your Choices and Rights</h2>
              <p className="mb-2 text-sm">
                Depending on applicable law and the services you use, you may have rights relating to your personal information, including the ability to:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Access or review certain information associated with your account.</li>
                <li>Update available profile information.</li>
                <li>Request correction of inaccurate information.</li>
                <li>Delete your VHFMS customer account.</li>
                <li>
                  Request deletion of associated personal information, subject to applicable legal or legitimate retention requirements.
                </li>
              </ul>
              <p className="mt-2 text-xs text-slate-600">
                You can also contact us using the information provided below for privacy-related questions or requests.
              </p>
            </div>

            {/* 11. Changes to This Privacy Policy */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">11. Changes to This Privacy Policy</h2>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>
                  We may update this Privacy Policy from time to time to reflect changes to our services, technology, legal requirements, or privacy practices.
                </li>
                <li>
                  When we make changes, we will publish the updated Privacy Policy on this page and update the Effective Date.
                </li>
                <li>We encourage users to review this Privacy Policy periodically.</li>
              </ul>
            </div>

            {/* 12. Contact Us */}
            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">12. Contact Us</h2>
              <p className="mb-3 text-sm">
                If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:
              </p>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm space-y-1">
                <p className="font-semibold text-slate-900">Company: VHFMS (Venkateswara Hospitality & Facilities Management Services)</p>
                <p>Website: <a href="http://www.vhfms.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.vhfms.com</a></p>
                <p>Email: <a href="mailto:vhfms.info@gmail.com" className="text-blue-600 hover:underline">vhfms.info@gmail.com</a></p>
                <p>Phone: +91-9900590051</p>
              </div>
            </div>

            {/* 13. Consent */}
            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">13. Consent</h2>
              <p className="text-sm leading-relaxed">
                By accessing or using VHFMS services, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Where applicable, we will request appropriate consent before collecting or processing information that requires your consent.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
